import { addDays } from "date-fns/addDays";
import { getHours } from "date-fns/getHours";
import { getMinutes } from "date-fns/getMinutes";
import { nextMonday } from "date-fns/nextMonday";
import { setHours } from "date-fns/setHours";
import { setMinutes } from "date-fns/setMinutes";
import { startOfToday } from "date-fns/startOfToday";

import type { Task } from "../types";

/**
 * Where a postponed task lands.
 *
 * Anchored on TODAY, not on the task's own due date: "postpone to tomorrow" on a
 * task that was due three weeks ago has to mean tomorrow, or the row never
 * leaves the Overdue bucket however often it is clicked.
 *
 * The time of day is carried over from the existing due date, so a 09:00 call
 * stays a 09:00 call. `due_date` is nullable, so it falls back to now rather
 * than letting `new Date(null)` resolve to the epoch.
 *
 * Shared rather than written per surface: every list that offers postponing has
 * to agree on this, and the anchoring is exactly what was wrong before.
 */
const atTaskTime = (task: Task, day: Date) => {
  const timeSource = task.due_date ? new Date(task.due_date) : new Date();
  return setMinutes(
    setHours(day, getHours(timeSource)),
    getMinutes(timeSource),
  );
};

export const postponedToTomorrow = (task: Task) =>
  atTaskTime(task, addDays(startOfToday(), 1));

export const postponedToNextWeek = (task: Task) =>
  atTaskTime(task, addDays(startOfToday(), 7));

export const postponedToNextMonday = (task: Task) =>
  atTaskTime(task, nextMonday(startOfToday()));
